/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import { Box, Button, Callout, Flex, Grid, Text, TextField } from '@radix-ui/themes';
import { AlertTriangle, Download, PackageX, Search, SearchX } from 'lucide-react';

import { EmptyState } from '../../../../shared/EmptyState';
import { ErrorState } from '../../../../shared/ErrorState';
import { FilterSidebar } from '../../../../shared/FilterSidebar';
import { LoadingState } from '../../../../shared/LoadingState';
import { TablePagination } from '../../../../shared/TablePagination/TablePagination';
import { QuarantineTable } from './QuarantineTable';
import { buildFilterSections } from './quarantineFilterSections';
import { PAGE_SIZES, type FilterSection } from './quarantine.types';
import { QUARANTINE_STRINGS as STRINGS } from './quarantine.strings';
import { useQuarantineList } from './useQuarantineList';

import './QuarantineListView.scss';

export function QuarantineListView(): JSX.Element {
  const list = useQuarantineList();
  const { query, items, total, truncated, limit, loading, failed, error, activeCount, activeCountTruncated, hasData } = list;
  // Keep the previous rows mounted during a reload so focus stays on the control that changed.
  const firstLoad = loading && !hasData;
  const reloading = loading && hasData;

  const renderResults = () => {
    if (firstLoad) {
      return <LoadingState message={STRINGS.list.loading} />;
    }
    if (failed) {
      return (
        <ErrorState
          title={STRINGS.list.loadErrorTitle}
          message={error ?? ''}
          onRetry={list.retry}
          retryText={STRINGS.list.retry}
          retryAnalyticsId="nxrm-security-quarantine-retry-btn"
        />
      );
    }
    if (items.length === 0) {
      return list.hasActiveFilters ? (
        <EmptyState
          icon={SearchX}
          title={STRINGS.list.noMatchesTitle}
          description={STRINGS.list.noMatchesDescription}
          action={{
            label: STRINGS.list.clearFilters,
            onClick: list.clearAll,
            analyticsId: 'nxrm-security-quarantine-clear-filters-btn',
          }}
        />
      ) : (
        <EmptyState
          icon={PackageX}
          title={STRINGS.list.emptyTitle}
          description={STRINGS.list.emptyDescription}
        />
      );
    }
    return <QuarantineTable items={items} sortBy={query.sortBy} asc={query.asc} onSort={list.setSort} />;
  };

  const plus = truncated ? '+' : '';
  const announcement = !hasData || loading || failed ? '' : total === 0 ? STRINGS.list.noResults : STRINGS.results(`${total.toLocaleString()}${plus}`);

  return (
    <Flex direction="column" gap="4">
      <div role="status" aria-live="polite" className="quarantine-visually-hidden">
        {announcement}
      </div>
      <Flex align="center" justify="between" gap="4" wrap="wrap">
        {activeCount !== null && (
          <Text>
            {activeCount.toLocaleString()}
            {activeCountTruncated ? '+' : ''} {STRINGS.list.activelyInQuarantine}
          </Text>
        )}
        <Flex align="center" gap="3">
          <TextField.Root
            aria-label="Search quarantined components"
            placeholder={STRINGS.list.searchPlaceholder}
            value={list.searchInput}
            onChange={(event) => list.setSearchInput(event.target.value)}
          >
            <TextField.Slot>
              <Search size={16} aria-hidden="true" />
            </TextField.Slot>
          </TextField.Root>
          {!failed && (
            <Button asChild variant="soft">
              <a href={list.exportUrl} download data-analytics-id="nxrm-security-quarantine-csv-download-lnk">
                <Download size={16} aria-hidden="true" />
                {STRINGS.list.downloadCsv}
              </a>
            </Button>
          )}
        </Flex>
      </Flex>

      <Grid columns={{ initial: '1', sm: '250px 1fr' }} gap="4">
        <Box role="complementary" aria-label="Quarantine filters" className="quarantine-filters">
          <FilterSidebar
            sections={buildFilterSections(list.facets, query.filters)}
            onFilterChange={(id, values) => list.setFilter(id as FilterSection, values as string[])}
            onClear={list.clearFilters}
            disabled={firstLoad}
          />
        </Box>
        <Box
          role="region"
          aria-label="Quarantine results"
          aria-busy={reloading}
          className={reloading ? 'quarantine-results quarantine-results--busy' : 'quarantine-results'}
        >
          <Flex direction="column" gap="4">
            {truncated && (
              <Callout.Root color="amber" role="note">
                <Callout.Icon>
                  <AlertTriangle size={16} />
                </Callout.Icon>
                <Callout.Text>
                  {STRINGS.truncatedNotice(limit.toLocaleString())}
                </Callout.Text>
              </Callout.Root>
            )}
            {renderResults()}
            {total > 0 && !failed && (
              <TablePagination
                currentPage={query.page}
                totalPages={Math.max(1, Math.ceil(total / query.pageSize))}
                itemsPerPage={query.pageSize}
                totalItems={total}
                totalItemsSuffix={truncated ? '+' : undefined}
                pageSizeOptions={PAGE_SIZES}
                onPageChange={list.setPage}
                onItemsPerPageChange={list.setPageSize}
                mt="0"
              />
            )}
          </Flex>
        </Box>
      </Grid>
    </Flex>
  );
}
